import { useState } from "react";
import * as yup from "yup";
import MyDialogContent from "@/components/MyDialogContent";
import RHFWrapper from "@/components/RHFWrapper";
import RhfCustomDatepicker from "@/components/rhfFields/RhfCustomDatepicker";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import { toast } from "sonner";

function parseLocalDateValue(value: string): Date | null {
  const raw = String(value).trim();
  if (!raw) return null;
  const datePart = raw.includes("T") ? raw.split("T")[0]! : raw.slice(0, 10);
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function isFutureLocalDate(value: string): boolean {
  const selected = parseLocalDateValue(value);
  if (!selected) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selected > today;
}

type Props = {
  patientId: string;
  lastVisit?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (lastVisit: string) => void;
};

export function UpdateLastVisitDialog({
  patientId,
  lastVisit = "",
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpdateLastVisit = async (formData: { lastVisit: string }) => {
    if (!patientId || !formData.lastVisit) {
      toast.error("Please select a date");
      return;
    }

    if (isFutureLocalDate(formData.lastVisit)) {
      toast.error("Last visit date cannot be in the future");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setIsSubmitting(true);
      const lastVisitDate = new Date(formData.lastVisit);
      const { data } = await AxiosInstance().put(`/patients/${patientId}/last-visit`, {
        lastVisit: lastVisitDate.toISOString(),
      });
      const updatedLastVisit = (data as { data?: { lastVisit?: string } | null })?.data?.lastVisit as string | undefined || formData.lastVisit;
      onSuccess?.(updatedLastVisit);
      onOpenChange(false);
      resolveFn("Last visit date updated successfully");
    } catch (error) {
      rejectFn(error);
      toast.error("Failed to update last visit date");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent className="md:w-md" heading="Update Last Visit Date">
        <RHFWrapper
          key={lastVisit || "empty"}
          initialValues={{ lastVisit }}
          isValidate
          onSubmit={handleUpdateLastVisit}
          validateSchema={yup.object({
            lastVisit: yup
              .string()
              .required("Last visit date is required")
              .test(
                "not-future",
                "Last visit date cannot be in the future",
                (value) => !value || !isFutureLocalDate(value),
              ),
          })}
          className="space-y-4"
        >
          <RhfCustomDatepicker
            name="lastVisit"
            label="Last Visit Date"
            required
            isFutureDateDisabled={true}
            placeholder="Select last visit date"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </div>
        </RHFWrapper>
      </MyDialogContent>
    </Dialog>
  );
}
